import { DataSyncResponse, NavigationDataResponse } from '@/api/navigationApi/types.gen'
import { assign, fromPromise, setup } from 'xstate'
import { Actions, Events, RouteModuleState as State, NavigationState } from './types'

export const navigationMachine = setup({
  types: {
    context: {} as {
      vehicleId: string | undefined
      navData: NavigationDataResponse | null
      errorMessage: string | null
      updatedAt: number | null
      status: DataSyncResponse['status'] | null
      shouldStartDropoff: boolean
      secondsToDropoff: number
    },
    events: {} as Events,
    input: {} as {
      vehicleId: string | undefined
    },
  },
  actions: {
    [Actions.NotifySuccess]: ({ event: _event }, _params: { title: string; message: string }) => {},
    [Actions.NotifyError]: ({ event: _event }, _params: { title: string; message: string }) => {},
    updateSyncInfo: assign({
      updatedAt: ({ event }) => (event.type === 'SYNC_UPDATE' ? event.updatedAt : null),
      status: ({ event }) => (event.type === 'SYNC_UPDATE' ? event.status : null),
    }),
    updateNavContext: assign({
      navData: ({ event }) => (event.type === 'NAV_UPDATE' ? event.data : null),
      shouldStartDropoff: ({ event }) => (event.type === 'NAV_UPDATE' ? !!event.shouldStartDropoff : false),
      secondsToDropoff: ({ event }) => (event.type === 'NAV_UPDATE' ? event.secondsToDropoff : Infinity),
      updatedAt: () => Date.now(),
    }),
    reset: assign({
      vehicleId: undefined,
      navData: null,
      errorMessage: null,
      updatedAt: null,
      status: null,
      shouldStartDropoff: false,
      secondsToDropoff: Infinity,
    }),
  },
  actors: {
    pauseMutation: fromPromise(async (): Promise<{ success: boolean }> => ({ success: true })),
    startMutation: fromPromise(async (): Promise<{ success: boolean }> => ({ success: true })),
    resumeMutation: fromPromise(async (): Promise<{ success: boolean }> => ({ success: true })),
    skipMutation: fromPromise(async (): Promise<{ success: boolean }> => ({ success: true })),
    finishMutation: fromPromise(async (): Promise<{ success: boolean }> => ({ success: true })),
  },
  guards: {
    hasNotSyncedRecently: ({ context }) => {
      if (context.updatedAt === null) return true
      const twentySecondsAgo = Date.now() - 20000
      return context.updatedAt < twentySecondsAgo
    },
  },
}).createMachine({
  id: 'navigationLogic',
  initial: NavigationState.CheckingSync,
  context: ({ input }) => ({
    vehicleId: input?.vehicleId,
    navData: null,
    errorMessage: null,
    updatedAt: null,
    status: null,
    shouldStartDropoff: false,
    secondsToDropoff: Infinity,
  }),
  on: {
    RESET: {
      target: `.${NavigationState.CheckingSync}`,
      actions: ['reset'],
    },
    SYNC_UPDATE: {
      actions: ['updateSyncInfo'],
    },
    SYNC_ERROR: {
      actions: assign({ errorMessage: ({ event }) => event.error?.message ?? 'Sync Error' }),
    },
    NAV_ERROR: {
      actions: assign({ errorMessage: ({ event }) => event.error?.message ?? 'Nav Error' }),
    },
  },
  states: {
    [NavigationState.CheckingSync]: {
      entry: 'reset',
      on: {
        SYNC_UPDATE: [
          {
            guard: ({ event }) => event.status === 'InProgress',
            target: NavigationState.SyncInProgress,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'Finished',
            target: NavigationState.SyncFinished,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'Waiting',
            target: NavigationState.Monitoring,
            actions: ['updateSyncInfo'],
          },
        ],
        NAV_UPDATE: {
          actions: ['updateNavContext'],
        },
      },
      after: {
        15000: {
          target: `${NavigationState.Monitoring}.${State.CommunicationError}`,
        },
      },
    },

    [NavigationState.SyncInProgress]: {
      on: {
        SYNC_UPDATE: [
          {
            guard: ({ event }) => event.status === 'Waiting',
            target: NavigationState.Monitoring,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'Finished',
            target: NavigationState.SyncFinished,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'InProgress',
            target: NavigationState.SyncInProgress,
            actions: ['updateSyncInfo'],
          },
        ],
        NAV_UPDATE: { actions: ['updateNavContext'] },
      },
      after: {
        15000: {
          target: `${NavigationState.Monitoring}.${State.CommunicationError}`,
          guard: 'hasNotSyncedRecently',
        },
      },
    },

    [NavigationState.SyncFinished]: {
      on: {
        SYNC_UPDATE: [
          {
            guard: ({ event }) => event.status === 'Waiting',
            target: NavigationState.Monitoring,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'InProgress',
            target: NavigationState.SyncInProgress,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'Finished',
            actions: ['updateSyncInfo'],
          },
        ],
        NAV_UPDATE: { actions: ['updateNavContext'] },
      },
    },

    [NavigationState.Monitoring]: {
      initial: State.Initializing,
      on: {
        NAV_ERROR: {
          target: `.${State.CommunicationError}`,
          actions: assign({ errorMessage: ({ event }) => event.error?.message ?? 'Nav Error' }),
        },
        SYNC_UPDATE: [
          {
            guard: ({ event }) => event.status === 'InProgress',
            target: NavigationState.SyncInProgress,
            actions: ['updateSyncInfo'],
          },
          {
            guard: ({ event }) => event.status === 'Finished',
            target: NavigationState.SyncFinished,
            actions: ['updateSyncInfo'],
          },
          {
            target: NavigationState.Monitoring,
            actions: ['updateSyncInfo'],
          },
        ],
        NAV_UPDATE: [
          {
            // IF STATUS CHANGED: Re-route the machine (External Transition)
            guard: ({ context, event }) =>
              event.data.route_module_state !== context.navData?.route_module_state ||
              event.data.is_freeride !== context.navData?.is_freeride ||
              (event.data.driver === undefined) !== (context.navData?.driver === undefined),
            target: `.${State.Initializing}`,
            actions: ['updateNavContext'],
          },
          {
            // IF ONLY POSITION CHANGED: Just update context (Internal Transition)
            // No 'target' means no re-entry, which kills the lag.
            actions: ['updateNavContext'],
            reenter: false,
          },
        ],
      },
      states: {
        [State.Initializing]: {
          always: [
            {
              guard: ({ context }) => context.navData !== null && context.navData?.driver === undefined,
              target: State.NoDriverSelected,
            },
            { guard: ({ context }) => context.navData?.is_freeride === true, target: State.Freeride },
            {
              guard: ({ context }) => context.navData?.route_module_state === 'NoScheduledDrive',
              target: State.NoScheduledDrive,
            },
            { guard: ({ context }) => context.navData?.route_module_state === 'Paused', target: State.Paused },
            {
              guard: ({ context }) => context.navData?.route_module_state === 'Maintenance',
              target: State.Maintenance,
            },
            { guard: ({ context }) => context.navData?.route_module_state === 'Fetched', target: State.Fetched },
            { guard: ({ context }) => context.navData?.route_module_state === 'Finished', target: State.Finished },
            { guard: ({ context }) => context.navData?.route_module_state === 'Fetching', target: State.Fetching },
            { guard: ({ context }) => !!context.navData, target: State.Driving },
            {
              guard: ({ context }) => context.navData?.route_module_state === 'FetchingDrivers',
              target: State.FetchingDrivers,
            },
          ],
        },

        [State.NoDriverSelected]: {},
        [State.CommunicationError]: {
          on: {
            NAV_UPDATE: {
              target: State.Initializing,
              actions: assign({ errorMessage: null }),
            },
          },
        },
        [State.Maintenance]: {},
        [State.NoScheduledDrive]: {},
        [State.Fetching]: {},
        [State.FetchingDrivers]: {},
        [State.Fetched]: {
          on: {
            REQUEST_START: State.Starting,
          },
        },
        [State.Freeride]: {
          on: {
            REQUEST_RESUME: State.Resuming,
            REQUEST_PAUSE: State.Pausing,
          },
        },
        [State.Driving]: {
          on: {
            REQUEST_PAUSE: State.Pausing,
            REQUEST_SKIP: State.Skipping,
          },
        },
        [State.Paused]: {
          on: {
            REQUEST_RESUME: State.Resuming,
            REQUEST_SKIP: State.Skipping,
          },
        },
        [State.Pausing]: {
          on: { NAV_UPDATE: { actions: ['updateNavContext'], reenter: false } },
          invoke: {
            src: 'pauseMutation',
            onDone: State.Paused,
            onError: State.Driving,
          },
        },
        [State.Starting]: {
          on: { NAV_UPDATE: { actions: ['updateNavContext'], reenter: false } },
          invoke: {
            src: 'startMutation',
            onDone: State.Driving,
            onError: State.Paused,
          },
        },
        [State.Resuming]: {
          on: { NAV_UPDATE: { actions: ['updateNavContext'], reenter: false } },
          invoke: {
            src: 'resumeMutation',
            onDone: State.Driving,
            onError: State.Paused,
          },
        },
        [State.Skipping]: {
          on: { NAV_UPDATE: { actions: ['updateNavContext'], reenter: false } },
          invoke: {
            src: 'skipMutation',
            onDone: State.Driving,
            onError: State.Driving,
          },
        },
        [State.Finished]: {
          on: { REQUEST_FINISH: State.Finishing },
        },
        [State.Finishing]: {
          on: { NAV_UPDATE: { actions: ['updateNavContext'], reenter: false } },
          invoke: {
            src: 'finishMutation',
            onDone: State.Fetching,
            onError: State.Finished,
          },
        },
      },
      after: {
        15000: {
          target: `.CommunicationError`,
          guard: 'hasNotSyncedRecently',
        },
      },
    },
  },
})
